import { useCallback, useEffect, useRef, useState } from 'react';
import type { FairlightBridge } from './types';
import { decodeBase64, type PlatformAdapter } from './platform';

export type PageRExportView = {
  phase: 'idle' | 'starting' | 'rendering' | 'packaging' | 'complete' | 'cancelling' | 'error';
  mode: 'sum' | 'keyboards'; renderedFrames: number; totalFrames: number; error?: string;
};
type ExportResponse = PageRExportView & {
  sessionId: string; done?: boolean;
  artifact?: { filename: string; mime: string; dataBase64: string };
};
const idle: PageRExportView = { phase: 'idle', mode: 'sum', renderedFrames: 0, totalFrames: 0 };
export const exportActive = (phase: PageRExportView['phase']) => ['starting', 'rendering', 'packaging', 'cancelling'].includes(phase);
// Yield between actual worker blocks so progress/cancel can be painted and handled.
const yieldToUI = () => new Promise<void>(resolve => setTimeout(resolve, 0));

export function usePageRExport(bridge: FairlightBridge, platform: PlatformAdapter) {
  const [view, setView] = useState<PageRExportView>(idle);
  const generation = useRef(0), session = useRef<string | null>(null), active = useRef(false);
  const cancel = useCallback(async () => {
    const token = ++generation.current;
    const id = session.current; session.current = null;
    if (active.current) setView(value => ({ ...value, phase: 'cancelling' }));
    if (id) await bridge.request({ action: 'page_r_export_cancel', sessionId: id }).catch(() => undefined);
    if (token === generation.current) { active.current = false; setView(idle); }
  }, [bridge]);
  useEffect(() => () => {
    generation.current++;
    const id = session.current; session.current = null;
    if (id) void bridge.request({ action: 'page_r_export_cancel', sessionId: id }).catch(() => undefined);
  }, [bridge]);
  const start = async (id: number, mode: 'sum' | 'keyboards', stopPlayback: () => Promise<void>) => {
    if (active.current) return;
    active.current = true;
    const token = ++generation.current;
    const current = () => token === generation.current;
    let owned: string | null = null;
    setView({ ...idle, mode, phase: 'starting' });
    try {
      await stopPlayback();
      if (!current()) return;
      let result = await bridge.request<ExportResponse>({ action: 'page_r_export_start', id, mode });
      owned = result.sessionId;
      if (!current()) return;
      session.current = owned;
      while (current()) {
        setView({ phase: result.phase, mode, totalFrames: result.totalFrames, renderedFrames: result.renderedFrames });
        if (result.done) {
          if (!result.artifact) throw new Error('PAGE R export returned no artifact');
          const artifact = result.artifact;
          platform.download(artifact.filename, decodeBase64(artifact.dataBase64), artifact.mime);
          session.current = null; owned = null;
          break;
        }
        await yieldToUI();
        if (!current()) return;
        result = await bridge.request<ExportResponse>({ action: 'page_r_export_next', sessionId: owned });
      }
    } catch (error) {
      if (current()) setView(value => ({ ...value, phase: 'error', error: error instanceof Error ? error.message : String(error) }));
    } finally {
      if (owned) await bridge.request({ action: 'page_r_export_cancel', sessionId: owned }).catch(() => undefined);
      if (current()) { session.current = null; active.current = false; }
    }
  };
  return { view, start, cancel };
}
